import {derivedAsync} from 'ngxtension/derived-async';
import {map, of, tap} from 'rxjs';
import {configureIndexedDb, useDevtools, useStorePersistence, useStoreStatus} from 'signalstory';

import {inject, Injectable} from '@angular/core';
import {IValidationRule} from '@core/api/data-access';
import {DEFAULT_INDEXED_DB_SIGNAL_STORE_NAME, DEFAULT_INDEXED_DB_SIGNAL_STORE_VERSION, SignalStoreV0} from '@shared-types';
import {deepCompare, ResetableStore} from '@utils';

import {ValidationRuleModel} from '../models';
import {ValidationRulesService} from '../services';
import {ValidationRulesStoreState} from './validation-rules.models';

import IValidationRulesState = ValidationRulesStoreState.IValidationRulesState;
import validationRulesInitialState = ValidationRulesStoreState.validationRulesInitialState;

@ResetableStore({resetOn: 'logout'})
@Injectable({providedIn: 'root'})
export class ValidationRulesStore extends SignalStoreV0<IValidationRulesState> {
	/* ----------------------------------------- Services and Providers ----------------------------------------- */
	private readonly validationRulesService: ValidationRulesService = inject(ValidationRulesService);
	/* ----------------------------------------------  Constructor ---------------------------------------------- */
	constructor() {
		super({
			name: 'Validation Rules Store',
			initialState: validationRulesInitialState,
			plugins: [
				useDevtools(),
				useStoreStatus(),
				useStorePersistence(
					configureIndexedDb({
						dbName: DEFAULT_INDEXED_DB_SIGNAL_STORE_NAME,
						dbVersion: DEFAULT_INDEXED_DB_SIGNAL_STORE_VERSION,
						objectStoreName: 'validation-rules',
						handlers: {
							onSuccess: () => {
								// Your success handler is called after successfull initialization
							},
							onBlocked: () => {
								// Your blocked handler is called
								// when an open connection to a database is blocking a versionchange transaction
							},
							onInitializationError: () => {
								// Your error handler is called in the case of an initialization error
								debugger;
							},
						},
						projection: {
							onWrite: function (state: IValidationRulesState): IValidationRulesState {
								return state;
							},
							onLoad: function (projection: IValidationRulesState): IValidationRulesState {
								return projection;
							},
						},
					}),
				),
			],
		});
	}

	public readonly SELECTORS$ = {
		inLoadingState: derivedAsync(() => this.state()?.loading),
		selectValidationRulesArray: derivedAsync(() => {
			if (this.state()?.validations) return of(this.state().validations);
			return this.ACTIONS$.loadValidationRules();
		}),
	};

	public readonly ACTIONS$ = {
		addValidationRule: (validationRule: IValidationRule) => {
			return this.validationRulesService.addValidationRule(validationRule).pipe(
				tap(newValidationRule => {
					this.mutate(state => {
						if (!state.validations) state.validations = [];
						state.validations.push(newValidationRule);
						state.validations = [...state.validations];
					}, 'Validation Rule Added');
				}),
			);
		},
		deleteValidationRule: (validationRuleKey: string) => {
			return this.validationRulesService.deleteValidationRule(validationRuleKey).pipe(
				tap(() => {
					this.mutate(state => {
						state.validations = state.validations.filter(validation => validation.key !== validationRuleKey);
					}, 'Validation Rule Deleted');
				}),
			);
		},
		updateValidationRule: (validationRule: IValidationRule) => {
			return this.validationRulesService.updateValidationRule(validationRule).pipe(
				tap(updatedValidationRule => {
					this.mutate(state => {
						state.validations = state.validations.map(validation => {
							if (validation.key === updatedValidationRule.key) return updatedValidationRule;
							return validation;
						});
					}, 'Validation Rule Updated');
				}),
			);
		},
		loadValidationRules: () => {
			if (this.state()?.validations) return of(this.state().validations);
			return this.validationRulesService.getValidationRulesList().pipe(
				tap(validationRules => {
					this.mutate(state => {
						state.validations = validationRules;
					}, 'Validation Rules Loaded');
				}),
			);
		},
		loadValidationRulesModelMap: () => {
			return this.ACTIONS$.loadValidationRules().pipe(
				map(validationRules => {
					const validationRulesMap: Map<string, ValidationRuleModel> = new Map<string, ValidationRuleModel>();
					validationRules.forEach((validationRule: IValidationRule) => {
						validationRulesMap.set(validationRule.key, new ValidationRuleModel(validationRule));
					});
					return validationRulesMap;
				}),
			);
		},
		reloadValidationRules: () => {
			return this.validationRulesService.getValidationRulesList().pipe(
				tap(validationRules => {
					this.mutate(state => {
						state.validations = validationRules;
					}, 'Validation Rules Reloaded');
				}),
			);
		},
	};

	/* ------------------------------------------------  Methods ------------------------------------------------ */
	public reset(): void {
		if (deepCompare(this.state(), validationRulesInitialState)) return;
		this.set(validationRulesInitialState, 'State Reset');
	}
}
